/*
  Interaktivno učenje — seznam vsebin.

  Vsaka vsebina ima najprej razlago (besedilo po učbeniku in po Lukovem
  pouku), nato interaktivni del. Interaktivni del se naloži šele, ko ga
  tečajnik odpre, zato ta seznam ne vleče three.js ali česa drugega.

  Nova vsebina = nov vnos tukaj + modul, ki ga naloži `load`.
*/

import { MODULE as BOCNI } from './bocni/module.js';
import { MODULE as KONTROLA } from './kontrola/module.js';
import { MODULE as KROZENJE } from './krozenje/module.js';
import { MODULE as NIHANJE } from './nihanje/module.js';
import { MODULE as POBOCJE } from './pobocje/module.js';
import { MODULE as POSPESEVALNIK } from './pospesevalnik/module.js';
import { MODULE as VZVRATNI } from './vzvratni/module.js';
import { MODULE as ZADNJI } from './zadnji/module.js';
import { MODULE as ZAPIRANJE } from './zapiranje/module.js';

/* Vsebine, ki živijo v svoji mapi (glej NOV-MODUL.md). */
const NOVI = [BOCNI, KONTROLA, KROZENJE, NIHANJE, POBOCJE, POSPESEVALNIK, VZVRATNI, ZADNJI, ZAPIRANJE];

const OSNOVNI = [
  {
    id: 'vzlet',
    title: 'Dvig krila in vzlet',
    short: 'Polnjenje, dvig in stabilizacija krila, pospeševanje in vzlet.',
    meta: '3D simulacija · 4 stopnje',
    vaje: 'Začetni tečaj, vaji 2 in 3',
    icon: 'mountain',
    intro:
      'Vzlet je motorično najzahtevnejša faza poleta. Sestavljen je iz polnjenja, dviga in stabilizacije krila ter pospeševanja do odlepitve. Na tečaju ga vadiš po sklopih, zato ga tudi tukaj.',
    steps: [
      ['Polnjenje', 'Roke so v odročenju, rahlo nazaj in navzdol, v smeri nosilnih trakov. Krilo polniš s telesom: z naslonom naprej obremeniš vpetje in prek njega vse vrvice hkrati. Komand med polnjenjem ne vlečeš.'],
      ['Dvig in stabilizacija', 'Krilo mora priti nad tebe. Ko je skoraj nad glavo, spustiš A trakova in zavreš, da te ne prehiti. Nato ga stabiliziraš: krilo je obrnjeno v veter, ti si pod sredino krila.'],
      ['Vodenje krila', 'Počakaš, da je krilo nad tabo, šele nato tečeš. Roke so skoraj iztegnjene zadaj, naslon naprej. V šibkem vetru več teka, v močnem si bolj pokončen.'],
      ['Pospeševanje in vzlet', 'Enakomerno pospešuješ in postopno popuščaš komande. Do konca jih ne spustiš: vzletiš z delno zavrtim krilom, okoli 15 do 35 %.'],
      ['Po odlepitvi', 'Še korak ali dva v prazno, šele nato se namestiš v sedež.'],
    ],
    how: 'Z drsnikom desno upravljaš komande: zgoraj so roke visoko, spodaj je krilo zavrto do konca. Naslon, tek in korake pod krilo simulacija naredi sama. Gumb Zrak pokaže, kako zrak obteka krilo. Najprej si oglej pravilno izvedbo, nato pojdi po stopnjah.',
    levels: ['L1', 'L2', 'L3', 'L4'],
    store: 'perut-vzlet-done',
    load: () => import('./launch/sim.js').then((m) => (root, ctx) => m.mountLaunchSim(root, { module: 'vzlet', ...ctx })),
  },
  {
    id: 'levo',
    title: 'Levo in desno',
    short: 'Ko krilo uhaja vstran: stopiš pod krilo in hkrati kontriraš s komando.',
    meta: '3D simulacija',
    vaje: 'Začetni tečaj, vaja 2',
    icon: 'compass',
    intro:
      'Krilo, ki gre na stran, samo od sebe ne pride nazaj. Popraviš ga s telesom in komando hkrati: stopiš pod krilo in že med korakom dodaš malo nasprotne komande.',
    steps: [
      ['Pod krilo', 'Ko krilo začne uhajati levo, stopiš levo pod krilo. Ker tečeš naprej, je korak diagonalno naprej, ne 90° vstran.'],
      ['Hkrati kontra komanda', 'Že med korakom dodaš malo nasprotne komande: krilo gre levo, desna komanda. Ne čakaš, da si pod krilom. Ko je krilo na sredini, jo spustiš.'],
      ['Zamik', 'Krilo se na komando odzove z eno do dvema sekundama zamika. Zato malo in pravočasno, ne veliko in pozno.'],
      ['Najpogostejša napaka', 'Samo kontriranje s komando, brez koraka pod krilo. Sama komanda krila ne pripelje nazaj, korak in komanda gresta skupaj.'],
    ],
    how: 'Levi drsnik je telo: korak levo ali desno. Desni drsnik je kontra komanda. Uporabljaš ju hkrati, ob spustu se vrneta na sredino. Pilot sam teče naprej, osnovno zaviranje naredi simulacija.',
    levels: ['L6'],
    store: 'perut-vzlet-done',
    load: () => import('./launch/sim.js').then((m) => (root, ctx) => m.mountLaunchSim(root, { module: 'levo', ...ctx })),
  },
  {
    id: 'prekinitev',
    title: 'Prekinitev vzleta',
    short: 'Ko krilo ni stabilno: zavreš eno stran in tečeš bočno.',
    meta: '3D simulacija',
    vaje: 'Začetni tečaj, vaja 2',
    icon: 'x',
    intro:
      'Prekinitev vzleta ni neuspeh, je pravilna odločitev. Na vzletišču jo izvedeš s podrtjem krila na stran.',
    steps: [
      ['Odločitev', 'Če krilo ni stabilno nad tabo, ne nadaljuješ. Učenci največkrat ne zgrešijo tehnike, ampak odločitev: preveč si želijo poleteti.'],
      ['Ena komanda', 'Močno zavreš eno stran krila. Krilo zavije v to stran in izgubi hitrost.'],
      ['Tek v stran', 'Tek preusmeriš bočno, prečno na strmino, po možnosti v hrib. Tako se ustaviš in razbremeniš krilo.'],
      ['Ne obe hkrati', 'Če na strmini zavreš celo krilo, te lahko še dvigne. Zato prekineš z eno komando.'],
      ['Na katero stran', 'Kjer je več prostora in manj ovir. Pri bočnem vetru proti vetru. Na vsakem vzletišču to veš že pred vzletom.'],
    ],
    how: 'Pilot sam dvigne in vodi krilo. Ko krilo postane nestabilno, se oglasi opozorilo. Takrat z enim drsnikom potegni eno komando do dna: levi drsnik je leva komanda, desni desna.',
    levels: ['L7'],
    store: 'perut-vzlet-done',
    load: () => import('./launch/sim.js').then((m) => (root, ctx) => m.mountLaunchSim(root, { module: 'prekinitev', ...ctx })),
  },
  {
    id: 'zavoji',
    title: 'Zavoji',
    short: 'Komanda in nagib telesa: počasi vlečeš, nagneš se v smer zavoja.',
    meta: '3D simulacija · 2 stopnji',
    vaje: 'Začetni tečaj, vaja 4',
    icon: 'refresh',
    intro:
      'Jadralno padalo krmiliš z vlečenjem krmilnih vrvic in z nagibom telesa v sedežu. Z vrvico določaš, katera polovica krila leti počasneje, z nagibom sledeš nagibu krila in preneseš težo na notranjo stran zavoja.',
    steps: [
      ['Roke', 'V zraku so roke brez komand visoko, pri ušesih. Komando vlečeš dol največ do karabina — nižje je pregloboko.'],
      ['Počasi in enakomerno', 'Notranjo komando vlečeš počasi in enakomerno, nikoli sunkovito. Sunkovit poteg zaniha pilota vstran, pregloboko vlečenje pa te približa negativnemu zavoju.'],
      ['Nagib telesa', 'Hkrati se v sedežu nagneš v smer zavoja. Z nagibom dosežeš enakomerno obremenitev nosilnih vrvic, krilo gre lažje v zavoj in ne niha. Če sediš pokonci, si pomagaš tako, da zunanjo nogo prekrižaš čez notranjo.'],
      ['Samo nagib', 'Brez komand, samo z nagibom, krilo počasi in v velikem loku spremeni smer. V praksi zavoj izvajaš s kombinacijo komande in nagiba.'],
      ['Izhod', 'Malo pred ciljem postopno popuščaš komando in se poravnaš v sedežu. Izhod je enako postopen kot vhod.'],
      ['Propadanje', 'V zavoju propadaš hitreje kot v ravnem letu. Bolj ko je zavoj oster, večji je nagib krila in hitreje izgubljaš višino.'],
    ],
    how: 'Levi in desni drsnik sta leva in desna komanda: ostaneta, kjer ju pustiš. Brez komand so roke pri ušesih, črta na drsniku je karabin — nižje je pregloboko. Spodnji drsnik je nagib telesa, ob spustu se vrne na sredino. Kompas desno zgoraj pokaže, koliko je še do cilja.',
    levels: ['nagib', 'zavoji'],
    store: 'perut-letenje-done',
    load: () => import('./flight/sim.js').then((m) => (root, ctx) => m.mountFlightSim(root, { module: 'zavoji', ...ctx })),
  },
  {
    id: 'pristanek',
    title: 'Pristanek',
    short: 'Osmice na razdeljenem polju, šolski krog in izravnava proti vetru.',
    meta: '3D simulacija · 3 stopnje',
    vaje: 'Začetni in nadaljevalni tečaj',
    icon: 'wind',
    intro:
      'Pristajalni postopek sestoji iz pristajalnega manevra in pristanka. Z manevrom zavzameš pozicijo, ki je najbolj ugodna za pristanek. Vsi manevri se končajo z ravnim letom proti vetru.',
    steps: [
      ['Veter', 'Na vetrni vreči preveriš smer in jakost vetra. Vreča kaže, kam piha veter; bolj ko je napeta, močnejši je veter. Pristajaš proti vetru.'],
      ['Izgubljanje višine', 'Polje si razdeliš na dva dela. Nad delom za izgubljanje višine delaš osmice prečno na veter in obračaš proti vetru. Zavoj je komanda in nagib telesa v sedežu v smer zavoja. Nad pristajalnim delom višine ne izgubljaš.'],
      ['Šolski krog', 'Ko pristaja več pilotov hkrati: izhodiščna točka ob pristajalnem prostoru, let z vetrom, prečno na veter in proti vetru. Z dolžino prečnega leta uravnavaš višino.'],
      ['Let proti vetru', 'Zadnjih 50 m letiš naravnost proti vetru, z rahlo zavrtim krilom, okoli 20 %. Pred izravnavo nikoli čez 60 %. Nikoli ne pristajaš iz zavoja.'],
      ['Iz sedeža', 'Najpozneje na 10 m greš iz sedeža v pokončno držo, rahlo nagnjen naprej. Pokonci imaš večji upor, krilo leti malo počasneje.'],
      ['Izravnava in tek', 'Tik nad tlemi postopno zaviraš do konca in že začneš teči. Ob dotiku stečeš naprej s krilom nad sabo, nato ga podreš nazaj, v veter.'],
    ],
    how: 'Desni drsnik so komande: ostanejo, kjer jih pustiš. Levi drsnik je zavoj, komanda in nagib telesa hkrati. Gumb Iz sedeža te postavi pokonci. Zemljevid levo zgoraj pokaže polje, veter in krožec, kje bi pristal, če zdaj zaviješ v veter. Najprej si oglej pravilno izvedbo.',
    levels: ['izravnava', 'osmice', 'krog'],
    store: 'perut-pristanek-done',
    load: () => import('./landing/sim.js').then((m) => (root, ctx) => m.mountLandingSim(root, ctx)),
  },
  {
    id: 'usesa',
    title: 'Ušesa',
    short: 'Hitro spuščanje: zložena konca krila, pospeševalnik, zavoj z nagibom.',
    meta: '3D simulacija',
    vaje: 'Nadaljevalni tečaj, vaja 6',
    icon: 'down',
    note: 'Ušesa delaš samo visoko, nikoli nizko ali ob pobočju. Spadajo v nadaljevalni tečaj — na začetnem tečaju, ko letimo nizko, jih nikoli ne delaš.',
    intro:
      'Ušesa so postopek hitrega spuščanja. Z obojestranskim potegom zunanjih vrvic A linije simetrično zapreš konca krila. Krilo ima manjšo nosilno površino, večji tlak in propada okoli 3,5 m/s, a še vedno leti naprej in je vodljivo.',
    steps: [
      ['Samo visoko', 'Ušesa so za nadaljevalni tečaj in višinske lete. Nikoli jih ne delaš nizko ali ob pobočju.'],
      ['Zunanji A vrvici', 'A vrvice so na vseh padalih razcepljene na srednje in zunanje. Zunanje so pripete na zunanji del krila in imajo svoj trak.'],
      ['Poteg', 'Komandi obdržiš v rokah. S tako rokama primeš zunanji A vrvici in ju potegneš navzdol, zunanja konca krila se zložita.'],
      ['Pospeševalnik', 'Pohodiš pospeševalnik. Krilo z ušesi je tako stabilnejše, hitrejše in učinkoviteje zmanjšuje višino.'],
      ['Vodenje', 'Z ušesi vodiš z nagibom telesa, komand ne uporabljaš.'],
      ['Velika ušesa', 'Pri velikih ušesih se lahko poveča vpadni kot in krilo preide v prevlečeni let, propadanje do 10 m/s. Pospeševalnik to prepreči.'],
      ['Izhod', 'Spustiš pospeševalnik in A vrvici. Krilo se napolni, odpiranje lahko pospešiš s potegi komand. Ušesa spustiš dovolj visoko, preden začneš pristajalni manever.'],
    ],
    how: 'Desni drsnik so ušesa (zunanji A vrvici): ostane, kjer ga pustiš. Levi drsnik dol je pospeševalnik. Spodnji drsnik je nagib telesa. Pogled od hrbta pokaže, da roki vlečeta zunanji A vrvici (rožnati). Desno zgoraj vidiš višino in propadanje.',
    levels: ['usesa'],
    store: 'perut-letenje-done',
    load: () => import('./flight/sim.js').then((m) => (root, ctx) => m.mountFlightSim(root, { module: 'usesa', ...ctx })),
  },
  {
    id: 'negativni',
    title: 'Negativni zavoj',
    short: 'Zakaj komande nikoli ne vlečeš sunkovito in pregloboko.',
    meta: '3D prikaz · 2 stopnji',
    vaje: 'Varnost',
    icon: 'alert',
    warn: true,
    intro:
      'Negativni zavoj je enostransko odtrganje vzgona zaradi prevelikega vpadnega kota na eni strani krila. Prevlečena stran nima vzgona, druga leti naprej, zato se krilo vrti okoli navpične osi in strmoglavlja.',
    steps: [
      ['Kdaj nastane', 'Če eno komando potegneš prehitro in premočno. Ali če z močno zavrtim krilom zaviješ, ne da bi prej pridobil hitrost. Tudi pri nesimetričnem izhodu iz full stalla.'],
      ['Nevarnosti', 'Izguba orientacije, prekrižanje nosilnih trakov in blokirane komande, padec kupole v stran ali pred pilota.'],
      ['Izhod', 'Prevlečeno stran popustiš. Formirano stran rahlo zavreš, okoli 20 %, težo preneseš na formirano stran, tisto, ki te prehiteva. Formirane strani ne zavreš preveč, sicer sledi full stall.'],
      ['Po izhodu', 'Ko se vrtenje ustavi, krilo sune naprej. Ulovi ga z zaviranjem.'],
      ['Kako se mu izogneš', 'Komando vlečeš počasi in enakomerno. Iz počasnega leta najprej popustiš obe komandi in pridobiš hitrost, šele nato zaviješ.'],
    ],
    how: 'Prva stopnja je samo prikaz: iz počasnega leta hitro potegni eno komando do konca, nato izvedi izhod. Druga stopnja: iz istega počasnega leta pravilno zavij. Levi in desni drsnik sta komandi, spodnji je nagib telesa.',
    levels: ['negativni', 'izogib'],
    store: 'perut-letenje-done',
    load: () => import('./flight/sim.js').then((m) => (root, ctx) => m.mountFlightSim(root, { module: 'negativni', ...ctx })),
  },
  {
    id: 'stall',
    title: 'Full stall',
    short: 'Zakaj v zraku nikoli ne zaviraš do konca.',
    meta: '3D prikaz',
    vaje: 'Varnost',
    icon: 'alert',
    warn: true,
    intro:
      'Full stall ali popolni zastoj nastane, ko krilo zavreš do konca. Zadnji rob se zapogne navzdol, vpadni kot preseže kritično mejo in zrak ne obteka več profila. Vzgon se poruši.',
    steps: [
      ['Kaj se zgodi', 'Krilo se podkvasto zapre in omahne za pilota, pilot zaradi vztrajnosti potuje naprej. Sistem pilot–krilo zaniha, vertikalna hitrost je 10 do 15 m/s.'],
      ['Ko komande spustiš', 'Krilo se napolni in z veliko hitrostjo sune naprej pred pilota. Lahko se čelno ali enostransko zapre, pilot lahko pade v kupolo.'],
      ['Zato', 'Full stall je na majhni višini smrtno nevaren. V zraku roke nikoli niso popolnoma iztegnjene navzdol in krila nikoli ne zaviraš do konca. Namerno ga ne delaš.'],
    ],
    how: 'To je samo prikaz. Letiš 150 m nad terenom. Počasi potegni drsnik do dna, zadrži in ga spusti. Z gumbom Zrak vidiš, kako se tok odtrga od krila.',
    levels: ['L5'],
    store: 'perut-vzlet-done',
    load: () => import('./launch/sim.js').then((m) => (root, ctx) => m.mountLaunchSim(root, { module: 'stall', ...ctx })),
  },
];

/*
  Vrstni red sledi šolanju: priprava in vzlet, let, pristanek, nadaljevalne
  vaje, nato varnostne vsebine (negativni zavoj, full stall) na koncu.
*/
const VRSTNI_RED = [
  'kontrola', 'vzlet', 'levo', 'prekinitev', 'vzvratni',
  'zavoji', 'bocni', 'pristanek', 'pobocje', 'krozenje', 'nihanje', 'pospesevalnik',
  'usesa', 'zadnji', 'zapiranje', 'negativni', 'stall',
];

/** Skupine na seznamu: s 17 vsebinami en sam seznam ne pove, kje začneš. */
export const SKUPINE = [
  { key: 'tla', label: 'Na tleh', ids: ['kontrola', 'vzlet', 'levo', 'prekinitev', 'vzvratni'] },
  { key: 'zrak', label: 'V zraku', ids: ['zavoji', 'bocni', 'pristanek', 'pobocje', 'krozenje', 'nihanje', 'pospesevalnik', 'usesa', 'zadnji'] },
  { key: 'varnost', label: 'Varnost', ids: ['zapiranje', 'negativni', 'stall'] },
];

export const MODULES = [...OSNOVNI, ...NOVI].sort((a, b) => {
  const ia = VRSTNI_RED.indexOf(a.id);
  const ib = VRSTNI_RED.indexOf(b.id);
  return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
});

/** Katere stopnje je tečajnik na tej napravi že opravil. */
export function doneLevels(key = 'perut-vzlet-done') {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) || '[]'));
  } catch {
    return new Set();
  }
}

/**
  Opravljene stopnje vsebine: na tej napravi ali zapisane v bazi (druga
  naprava). Vrne { done: [...], of: n }.
*/
export function moduleDone(m, rows = []) {
  const local = doneLevels(m.store);
  const db = new Set(rows.filter((r) => r.modul === m.id).map((r) => r.kljuc));
  const done = (m.levels ?? []).filter((k) => local.has(k) || db.has(k));
  return { done, of: (m.levels ?? []).length };
}
